<template>
    <div>
        <p v-if="!canSeeDetails" class="hint">Réservé aux <a target="_blank" :href="urlLinkAboPro">{{ textLinkAboPro }}</a></p>
        <template v-else>
            <p class="ccard-sub" style="padding:0 0 10px">Déclaration nutritionnelle obligatoire, dans l'ordre du règlement INCO. Les autres valeurs du référentiel se dévoilent en dessous.</p>
            <!-- Structure de la maquette (.nrow) : grille label / 100 g / 1 portion, bouton entre les lignes INCO et les autres valeurs -->
            <div class="nut">
                <div class="nrow hd"><span>Pour</span><em>100 g</em><em>1 portion</em></div>
                <div class="nrow key" v-if="has('calories') || has('caloriesJ')">
                    <span>Énergie</span>
                    <b>{{ v('caloriesJ', 100, 0) }} kJ<br><small>{{ v('calories', 100, 0) }} kcal</small></b>
                    <b>{{ v('caloriesJ', false, 0) }} kJ<br><small>{{ v('calories', false, 0) }} kcal</small></b>
                </div>
                <div v-for="r in mainRows" :key="r.key" class="nrow" :class="{sub: r.sub}" v-if="has(r.key)">
                    <span>{{ r.label }}</span>
                    <b>{{ v(r.key, 100) }}{{ r.unit ? ' ' + r.unit : '' }}</b>
                    <b>{{ v(r.key) }}{{ r.unit ? ' ' + r.unit : '' }}</b>
                </div>
                <button type="button" class="ghost-btn nmore" :aria-expanded="more ? 'true' : 'false'" aria-controls="nut-plus" @click="more = !more">
                    {{ more ? 'Masquer les autres valeurs' : 'Voir les autres valeurs' }}
                    <svg class="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
                </button>
                <div id="nut-plus" :hidden="!more">
                    <div class="nsep">Autres valeurs</div>
                    <div v-for="r in otherRows" :key="r.key" class="nrow" v-if="has(r.key)">
                        <span>{{ r.label }}</span>
                        <b>{{ v(r.key, 100) }}{{ r.unit ? ' ' + r.unit : '' }}</b>
                        <b>{{ v(r.key) }}{{ r.unit ? ' ' + r.unit : '' }}</b>
                    </div>
                    <div class="nsep">Minéraux et vitamines</div>
                    <div v-for="r in microRows" :key="r.key" class="nrow" v-if="has(r.key)">
                        <span>{{ r.label }}</span>
                        <b>{{ v(r.key, 100) }}{{ r.unit ? ' ' + r.unit : '' }}</b>
                        <b>{{ v(r.key) }}{{ r.unit ? ' ' + r.unit : '' }}</b>
                    </div>
                    <div class="nrow" v-if="dg(result, 'listAllergenesString')">
                        <span>Allergènes</span>
                        <b class="wide">{{ dg(result, 'listAllergenesString') }}</b>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import format from './format';

export default {
    name: 'NutritionCard',
    mixins: [format],
    props: {
        result: {type: Object, default: null},
        canSeeDetails: {type: Boolean, default: false},
        urlLinkAboPro: {type: String, default: ''},
        textLinkAboPro: {type: String, default: 'abonnements pro'},
    },
    data() {
        return {
            more: false,
            mainRows: [
                {key: 'matGrasses', label: 'Matières grasses', unit: 'g'},
                {key: 'acideGras', label: 'dont acides gras saturés', unit: 'g', sub: true},
                {key: 'glucides', label: 'Glucides', unit: 'g'},
                {key: 'sucre', label: 'dont sucres', unit: 'g', sub: true},
                {key: 'proteines', label: 'Protéines', unit: 'g'},
                {key: 'sel', label: 'Sel', unit: 'g'},
            ],
            otherRows: [
                {key: 'fibre', label: 'Fibres alimentaires', unit: 'g'},
                {key: 'sucreAjoutes', label: 'Sucres ajoutés', unit: 'g'},
                {key: 'sodium', label: 'Sodium', unit: 'mg'},
                {key: 'IG', label: 'Charge glycémique', unit: ''},
            ],
            microRows: [
                {key: 'calcium', label: 'Calcium', unit: 'mg'}, {key: 'cuivre', label: 'Cuivre', unit: 'mg'},
                {key: 'fer', label: 'Fer', unit: 'mg'}, {key: 'magnesium', label: 'Magnésium', unit: 'mg'},
                {key: 'manganese', label: 'Manganèse', unit: 'mg'}, {key: 'phosphore', label: 'Phosphore', unit: 'mg'},
                {key: 'potassium', label: 'Potassium', unit: 'mg'}, {key: 'selenium', label: 'Sélénium', unit: 'µg'},
                {key: 'zinc', label: 'Zinc', unit: 'mg'}, {key: 'vitamineD', label: 'Vitamine D', unit: 'µg'},
                {key: 'vitamineC', label: 'Vitamine C', unit: 'mg'}, {key: 'vitamineA', label: 'Vitamine A', unit: 'µg ER'},
                {key: 'omega6', label: 'Oméga 6', unit: 'g'}, {key: 'omega3', label: 'Oméga 3', unit: 'g'},
            ],
        };
    },
    methods: {
        has(key) {
            return this.isDef(this.dg(this.result, 'calcul.' + key + 'Ext100')) || this.isDef(this.dg(this.result, 'calcul.' + key + 'Ext'));
        },
        // Affichage seulement (le calcul stocké garde 3 décimales) : énergie à l'unité, autres valeurs à 2 décimales au plus
        v(key, per100, decimals = 2) {
            const val = this.dg(this.result, 'calcul.' + key + 'Ext' + (per100 ? '100' : ''));
            return this.isDef(val) ? this.round(val, decimals).toLocaleString('fr-FR', {maximumFractionDigits: decimals}) : '–';
        },
    },
};
</script>
